import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { vi } from './vi';
import { viAdmin } from './vi-admin';

export type Lang = 'en' | 'vi';
export const LANGS: Array<{ code: Lang; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'vi', label: 'Tiếng Việt' },
];

const deviceLang = (): Lang => {
  try {
    return navigator.language?.toLowerCase().startsWith('vi') ? 'vi' : 'en';
  } catch {
    return 'en';
  }
};

interface LangState {
  lang: Lang;
  /** True once the runner picked a language themselves (otherwise the account's language is adopted). */
  chosen: boolean;
  setLang: (lang: Lang, chosen?: boolean) => void;
}

/** The app's language, chosen in Profile → Settings (defaults to the phone's language). */
export const useLang = create<LangState>()(
  persist((set) => ({ lang: deviceLang(), chosen: false, setLang: (lang, chosen = true) => set((s) => ({ lang, chosen: s.chosen || chosen })) }), {
    name: 'rw:lang',
    storage: createJSONStorage(() => localStorage),
  }),
);

const DICTS: Record<Lang, Record<string, string> | null> = { en: null, vi: { ...vi, ...viAdmin } };

/**
 * Translates an English UI string. The English text is the key, so untranslated
 * strings simply stay English. `{name}` placeholders are filled from `vars`.
 */
export function translate(lang: Lang, text: string, vars?: Record<string, string | number>): string {
  let out = DICTS[lang]?.[text] ?? text;
  if (vars) for (const [k, v] of Object.entries(vars)) out = out.split(`{${k}}`).join(String(v));
  return out;
}

/** Locale for dates and times in the chosen language (undefined = the phone's own). */
export const locale = () => (useLang.getState().lang === 'vi' ? 'vi-VN' : undefined);

/** For code outside React (toasts from mutations, etc.). */
export const t = (text: string, vars?: Record<string, string | number>) => translate(useLang.getState().lang, text, vars);

/** Re-renders the component when the language changes. */
export function useT() {
  const lang = useLang((s) => s.lang);
  return (text: string, vars?: Record<string, string | number>) => translate(lang, text, vars);
}

/** `1 run` / `3 runs` with the right word in each language (Vietnamese has no plural). */
export function plural(lang: Lang, n: number, one: string, many: string) {
  return translate(lang, n === 1 ? one : many, { n });
}

// Keep <html lang> in sync for screen readers and hyphenation.
if (typeof document !== 'undefined') {
  document.documentElement.lang = useLang.getState().lang;
  useLang.subscribe((s) => (document.documentElement.lang = s.lang));
}
